import { FormControl, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { AnalyticEvents } from '@app/enums';
import { DataValue } from '@app/models/employee/data-value.model';
import { DataField } from '@app/models/settings/data-field.model';
import { Column } from '@app/modules/table/classes/column.class';
import { CellDisplay } from '@app/modules/table/enums/cell-display.enum';
import { InteractableColumn } from '@app/modules/table/interfaces/interactable-column.interface';
import { AnalyticService, AuthService } from '@app/services';
import debounce from 'lodash-es/debounce';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';
import { BadRequestError } from '@app/errors/http/400/bad-request.error';

const DEBOUNCE_TIME = 500;

const getErrorStateMatcherForNumericInput = (dataField: DataField): ErrorStateMatcher => {
    return {
        isErrorState(formControl: FormControl<number | null>): boolean {
            formControl.setValidators(Validators.min(dataField.min));
            formControl.setValidators(Validators.max(dataField.max));

            const numbersRegex = new RegExp('^[-]?\\d+$');
            formControl.setValidators(Validators.pattern(numbersRegex));

            return formControl && formControl.invalid;
        },
    };
};

const getErrorStateMatcherForTextInput = (dataField: DataField): ErrorStateMatcher => {
    return {
        isErrorState(formControl: FormControl<number | null>): boolean {
            if (dataField.min) {
                formControl.setValidators(Validators.minLength(dataField.min));
            }

            if (dataField.max) {
                formControl.setValidators(Validators.maxLength(dataField.max));
            }

            return formControl && formControl.invalid;
        },
    };
};

export class FieldValueColumn extends Column implements InteractableColumn {
    title: string;
    property = 'value';
    bindingProperty = 'value';
    sortable = false;
    type: CellDisplay = CellDisplay.inputText;
    showSaveActionIcons = true;
    errorStateMatcher: ErrorStateMatcher;

    onRowChange = debounce(async (dataValue: DataValue) => {
        try {
            await dataValue.param('company', this.auth.company.id).save();
            this.analyticsService?.trackEvent(AnalyticEvents.SettingsModuleUpdateCustomFieldValue);
        } catch (error) {
            // We expect saving to fail when the input is invalid. In that case we'll get a BadRequestError (400)
            // That's ok! Our table ui handles this, so we don't need to display an error notification on these types of errors.
            // But if it's not a BadRequestError, then rethrow.
            if (!(error instanceof BadRequestError)) {
                throw error;
            }
        }
    }, DEBOUNCE_TIME);

    constructor(
        protected auth: AuthService,
        protected dataField: DataField,
        protected analyticsService: AnalyticService
    ) {
        super();
        this.type = this.getColumnType();

        this.title = dataField?.name ?? 'Field Value';

        /*
         * If the field is a dropdown then we don't want to bind to value,
         * we'll want to bind to the selected dataFieldOptionId.
         * */
        if (this.dataField.type === DataFieldTypes.dropdown) {
            this.property = 'dataFieldOptionId';
            this.bindingProperty = 'dataFieldOptionId';
        }
    }

    getBindProperty(): string {
        return this.bindingProperty;
    }

    getPlaceHolderText(): string {
        return this.dataField.description ?? 'Please enter a value';
    }

    getDropdownOptions(): { label: string; value: number | string }[] {
        if (this.dataField.type === DataFieldTypes.dropdown) {
            return this.dataField.getFormattedDropdownOption({ allowNull: true });
        }

        return [
            { label: 'Yes', value: '1' },
            { label: 'No', value: '0' },
        ];
    }

    private getColumnType(): CellDisplay {
        const dateType = this.dataField.type === DataFieldTypes.date;
        const dropdownType =
            this.dataField.type === DataFieldTypes.boolean || this.dataField.type === DataFieldTypes.dropdown;
        const numberInputType = this.dataField.type === DataFieldTypes.number;
        const longTextType = this.dataField.type === DataFieldTypes.longText;

        if (numberInputType) {
            this.errorStateMatcher = getErrorStateMatcherForNumericInput(this.dataField);
            return CellDisplay.inputNumber;
        }

        if (longTextType) {
            return CellDisplay.textArea;
        }

        if (dateType) {
            return CellDisplay.datepicker;
        }

        if (dropdownType) {
            return CellDisplay.dropdown;
        }

        this.errorStateMatcher = getErrorStateMatcherForTextInput(this.dataField);
        return CellDisplay.inputText;
    }
}
